import { useEffect, useRef } from 'react';
import { getColorContrast } from '../../utils/helpers';

interface WebOCRSDKProps {
    ocrToken: string;
    sessionId: string;
    clientThemeSettings: any;
    documentTypes: string[];
    documentNumber?: string;
    imageKey?: string;
    reuseDocument?: boolean;
    validateLivenessMatch?: boolean;
    onSuccess: (ocrKeys: Array<{ ocr_key: string; template: string }>) => void;
    onError?: (err: any) => void;
}

const resolveThemeConfig = (clientThemeSettings: any) => {
    if (clientThemeSettings?.palette?.primary) {
        return {
            companyLogo: clientThemeSettings.logo_url,
            primaryColor: clientThemeSettings.palette.primary,
        };
    }
    return {
        companyLogo: clientThemeSettings?.logo_url,
        backgroundColor: clientThemeSettings?.background_color,
        buttonColor: clientThemeSettings?.button_color,
        buttonFontColor: getColorContrast(clientThemeSettings?.button_color),
        modalBackgroundColor: '#F5F5F2',
        fontColor: '#334150',
    };
};

export const WebOCRSDK = ({
    ocrToken,
    sessionId,
    clientThemeSettings,
    documentTypes,
    documentNumber,
    imageKey,
    reuseDocument,
    validateLivenessMatch,
    onSuccess,
    onError,
}: WebOCRSDKProps) => {
    const instanceRef = useRef<any>(null);

    useEffect(() => {
        let cancelled = false;

        const themeConfig = resolveThemeConfig(clientThemeSettings);

        const webOCR = new (window as any).QiTechWebOCR.WebOCR(ocrToken, sessionId)
            .setThemeConfiguration(themeConfig)
            .setShowInstructionScreen(true)
            .setShowSuccessScreen(false)
            .setShowAllowedTemplatesScreen(false)
            .setClientApp("qi_sign_app");

        if (documentNumber) {
            webOCR.setDocumentNumber(documentNumber);
        }
        if (imageKey) {
            webOCR.setValidationImageKey(imageKey);
            if (reuseDocument) {
                webOCR.setReuseDocument();
            }
            if (validateLivenessMatch) {
                webOCR.setValidateLivenessFaceMatch();
            }
        }

        if (process.env.REACT_APP_ENV !== 'production') {
            webOCR.setSandboxEnvironment();
        }
        instanceRef.current = webOCR.build();

        webOCR.initialize(documentTypes)
            .then((result: any) => { if (!cancelled) onSuccess(result); })
            .catch((err: any) => { if (!cancelled) onError?.(err); });

        return () => {
            cancelled = true;
            instanceRef.current?.closeWebOCR?.();
            instanceRef.current = null;
        };
    }, []);

    return null;
};
